import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement, createUniqueId } from '../lib-element';
import { register, unregister, show, hide, type TooltipOwner } from './tooltip-controller';
import tooltipCss from './lib-tooltip.css';
import buttonCss from '../button/lib-button.css';

@customElement('lib-tooltip')
export class LibTooltip extends LibElement {
  protected override get hostFocusSelector(): string { return this.open && !this.hideCloseButton ? 'button' : ''; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(buttonCss), unsafeCSS(tooltipCss)];

  @property({ attribute: 'close-label' }) closeLabel = 'Close';
  @property() headline = '';
  @property({ attribute: 'icon-name' }) iconName = '';
  @property({ attribute: 'icon-url' }) iconUrl = '';
  @property({ type: Number }) position = 128;
  @property({ type: Number }) distance = 9;
  @property({ type: Number }) offset = 0;
  @property({ attribute: 'restriction-mode' }) restrictionMode = 'viewport';
  @property({ attribute: 'restriction-target' }) restrictionTarget = '';
  @property() width = '';
  @property({ attribute: 'min-width' }) minWidth = '';
  @property({ attribute: 'max-width' }) maxWidth = '';
  @property({ type: Boolean, attribute: 'hide-close-button' }) hideCloseButton = false;
  @property({ type: Boolean, attribute: 'keep-on-outside-click' }) keepOnOutsideClick = false;
  @property({ type: Boolean, attribute: 'keep-on-escape' }) keepOnEscape = false;
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ attribute: false }) target: HTMLElement | null = null;

  owner: TooltipOwner | null = null;
  #registeredTarget: HTMLElement | null = null;
  readonly #observer = new ResizeObserver(() => this.#position());
  readonly #interaction: TooltipOwner = {
    invokeMethodAsync: async (method, open, reason) => {
      this.open = open;
      this.emit('lib-open-change', { isOpen: open, reason });
      await this.owner?.invokeMethodAsync(method, open, reason);
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    if (!this.hasAttribute('role')) this.setAttribute('role', 'tooltip');
    if (!this.id) this.id = createUniqueId('lib-tooltip');
    this.#observer.observe(this);
    this.requestUpdate();
  }

  override disconnectedCallback(): void {
    unregister(this);
    this.#observer.disconnect();
    this.#registeredTarget = null;
    super.disconnectedCallback();
  }

  protected override updated(): void {
    for (const [name, value] of [['width', this.width], ['min-width', this.minWidth], ['max-width', this.maxWidth]]) {
      if (value) this.style.setProperty(name, /^-?\d+$/.test(value) ? `${value}px` : value);
      else this.style.removeProperty(name);
    }
    if (this.#registeredTarget && this.#registeredTarget !== this.target) unregister(this);
    this.#registeredTarget = this.target;
    if (!this.target) return;
    register(this, this.target, this.#options(), this.#interaction);
    this.#position();
  }

  #options() {
    return {
      position: this.position, distance: this.distance, offset: this.offset,
      restrictionMode: this.restrictionMode, restrictionTarget: this.restrictionTarget,
      closeOnOutsideClick: !this.keepOnOutsideClick, closeOnEscape: !this.keepOnEscape
    };
  }

  #position(): void {
    if (this.open && this.target) show(this, this.target, this.#options());
    else hide(this);
  }

  #close(): void {
    hide(this);
    void this.#interaction.invokeMethodAsync('OnTooltipInteraction', false, 'close');
  }

  override render() {
    const icon = this.iconUrl || (this.iconName
      ? new URL(`_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/${encodeURIComponent(this.iconName)}.svg`, document.baseURI).href : '');
    const iconStyle = `--icon: url("${encodeURI(icon).replaceAll('"', '%22')}")`;
    const closeIconStyle = `--icon: url("${new URL('_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/close.svg', document.baseURI).href}")`;
    return html`
      <span class="main" part="main">
        <slot name="icon">${icon ? html`<i aria-hidden="true" style=${iconStyle}></i>` : nothing}</slot>
        <span class="content">
          ${this.headline ? html`<span class="headline" part="headline">${this.headline}</span>` : nothing}
          <span class="body" part="body"><slot></slot></span>
        </span>
      </span>
      ${this.hideCloseButton ? nothing : html`<button class="lib-button lib-button-tertiary" type="button" aria-label=${this.closeLabel} part="close-button" @click=${this.#close}><i class="close" aria-hidden="true" style=${closeIconStyle}></i></button>`}
    `;
  }
}

export async function bindTooltip(element: LibTooltip | null, target: HTMLElement | null, owner: TooltipOwner, open: boolean | null): Promise<void> {
  if (!element) return;
  element.owner = owner;
  element.target = target;
  if (open !== null) element.open = open;
  await element.updateComplete;
}

export function unbindTooltip(element: LibTooltip | null): void {
  if (!element) return;
  element.owner = null;
  element.target = null;
  unregister(element);
}

declare global {
  interface HTMLElementTagNameMap {
    'lib-tooltip': LibTooltip;
  }
}
